import { ElementRef, Injectable } from '@angular/core';
import { IContentControlVideoSource } from '../../domain/content-control/i-content-control-video-source';
import {
  genericVertexShaderSource,
  videoFragmentShaderSource,
  CCRect,
  CCMosaicRect,
  CCBlurRect,
  CanvasBaseObject,
  CCdrawingAreaRect,
  PlacementOnCanvas,
  CanvasPoint,
} from '../../domain/content-control/canvas';
import { CanvasRectangle } from '../../domain/content-control/canvas/cc-rect';
import { ShapeCollection } from './shape-collection';
import {
  EffectProgram,
  BlackRectProgram,
  BlackCircleProgram,
  MosaicRectProgram,
  MosaicCircleProgram,
  BlurRectProgram,
  BlurCircleProgram,
} from './effect-programs';

/** Keys of the effect area corners map used for hit-testing. */
type EffectAreaCornerKey =
  | 'top-left'
  | 'top-middle'
  | 'top-right'
  | 'right-middle'
  | 'right-bottom'
  | 'bottom-middle'
  | 'bottom-left'
  | 'left-middle';

/** Result of hit-testing the effect area: a corner key, 'frame', or empty string (miss). */
type EffectAreaHitResult = EffectAreaCornerKey | 'frame' | '';

@Injectable()
export class ContentControlCanvasService {
  private static readonly TEXCOORDS_NORMAL = new Float32Array([
    0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 1,
  ]);
  private static readonly RTLCHARS =
    /[\u0591-\u07FF\u200F\u202B\u202E\uFB1D-\uFDFD\uFE70-\uFEFC]/;
  private static readonly TEXCOORDS_FLIPPED = new Float32Array([
    0, 1, 1, 1, 0, 0, 0, 0, 1, 1, 1, 0,
  ]);
  private static readonly SHARED_VERTICIES = new Float32Array([
    -1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1,
  ]);
  private static readonly EFFECT_AREA_CORNER_SIZE = 6;
  public static readonly EFFECT_AREA_ID = 1997;
  private _effectAreaDefaultPosition: PlacementOnCanvas = new PlacementOnCanvas(40, 40, 15, 15);

  // Named constants for subtitles and effect area UI
  private static readonly SUBTITLE_FONT_SIZE = 24;
  private static readonly SUBTITLE_LINE_HEIGHT = 50;
  private static readonly SUBTITLE_LINE_SPACING = 25;
  private static readonly SUBTITLE_BOTTOM_OFFSET = 100;
  private static readonly EFFECT_AREA_SQUARE_FACTOR = 0.15;
  private static readonly EFFECT_AREA_ASPECT_RATIO = 16 / 9;
  private static readonly EFFECT_AREA_DEFAULT_HEIGHT_PERCENT = 15;
  private static readonly GAUSSIAN_SIGMA_DIVISOR = 2.5;
  private static readonly EFFECT_AREA_FRAME_LINE_WIDTH = 2;
  private static readonly EFFECT_AREA_DEFAULT_LINE_WIDTH = 1;
  private static readonly EFFECT_AREA_CROSS_ARM_FACTOR = 0.1;
  private static readonly EFFECT_AREA_CROSS_ARM_MAX = 25;

  private _webGL_RendererContext: WebGL2RenderingContext = null;
  private _videoElement: IContentControlVideoSource;
  private _htmlCanvasElement: HTMLCanvasElement;
  private _drawingCanvas: HTMLCanvasElement;
  private _2D_RendererContext: CanvasRenderingContext2D;
  private _intervalMs: number;
  private _isInit = false;
  private _lastFrameTime: number = 0;
  private _renderRafId: number | null = null;
  private _videoSeekedHandler: EventListener | null = null;

  //multipass
  private _multiPassFrameBuffer: WebGLBuffer;
  private _pingPongTextures: [WebGLTexture, WebGLTexture];
  private _currentPingPongIndex: number = 0;

  // video
  private _videoProgram: WebGLProgram;
  private _webGLVertexArray: WebGLVertexArrayObject;
  private _webGLTexture: WebGLTexture;

  // effect programs
  private _blackRectProgram: BlackRectProgram;
  private _blackCircleProgram: BlackCircleProgram;
  private _mosaicRectProgram: MosaicRectProgram;
  private _mosaicCircleProgram: MosaicCircleProgram;
  private _blurRectProgram: BlurRectProgram;
  private _blurCircleProgram: BlurCircleProgram;

  // shape collections
  private _blackRectCollection = new ShapeCollection<CanvasRectangle>();
  private _blackCircleCollection = new ShapeCollection<CCRect>();
  private _mosaicRectCollection = new ShapeCollection<CCMosaicRect>(
    (existing, incoming) => { existing.density = incoming.density; },
  );
  private _mosaicCircleCollection = new ShapeCollection<CCMosaicRect>(
    (existing, incoming) => { existing.density = incoming.density; },
  );
  private _blurRectCollection = new ShapeCollection<CCBlurRect>(
    (existing, incoming) => { existing.sampleDistance = incoming.sampleDistance; },
  );
  private _blurCircleCollection = new ShapeCollection<CCBlurRect>(
    (existing, incoming) => { existing.sampleDistance = incoming.sampleDistance; },
  );

  //subtitles
  private _subtitles: Array<string[]> = [];
  private _scalingFactor: number = 1.0;

  //effect area
  public drawEffectAreaSVG = false;
  public drawMultiEffects: Array<CCdrawingAreaRect> = [];
  public drawEffectRect: CCdrawingAreaRect;
  public effectRectAreaCorners: Record<EffectAreaCornerKey, CanvasPoint> = {
    'top-left': new CanvasPoint(0, 0),
    'top-middle': new CanvasPoint(0, 0),
    'top-right': new CanvasPoint(0, 0),
    'right-middle': new CanvasPoint(0, 0),
    'right-bottom': new CanvasPoint(0, 0),
    'bottom-middle': new CanvasPoint(0, 0),
    'bottom-left': new CanvasPoint(0, 0),
    'left-middle': new CanvasPoint(0, 0),
  };

  get isInitialized() {
    return this._isInit;
  }

  getEffectAreaDefaultPosition(): PlacementOnCanvas {
    return this._effectAreaDefaultPosition;
  }
  get canvasWidth() {
    return this._webGL_RendererContext.canvas.width;
  }
  get canvasHeight() {
    return this._webGL_RendererContext.canvas.height;
  }
  get drawingCanvasWidthUnit() {
    return this.canvasWidth / 100;
  }
  get drawingCanvasHeightUnit() {
    return this.canvasHeight / 100;
  }
  get canvasesXOffsetRatio() {
    return this.canvasWidth / this._drawingCanvas.clientWidth;
  }
  get canvasesYOffsetRatio() {
    return this.canvasHeight / this._drawingCanvas.clientHeight;
  }

  public reInitVideoElement(videoSource: IContentControlVideoSource) {
    // The handler reference must be reused - removeEventListener with a fresh
    // .bind() is a no-op, leaking one 'seeked' listener per video element.
    if (this._videoElement && this._videoSeekedHandler) {
      this._videoElement.removeEventListener('seeked', this._videoSeekedHandler);
    }
    this._videoElement = videoSource;
    this._videoSeekedHandler = this.clearAllRenderingObjects.bind(this);
    this._videoElement.addEventListener('seeked', this._videoSeekedHandler);
  }

  public initWebGLService(
    videoSource: IContentControlVideoSource,
    htmlCanvasElementForWebGLRef: ElementRef<HTMLCanvasElement>,
    htmlCanvasElementFor2DRef: ElementRef<HTMLCanvasElement>,
    intervalMs: number,
  ) {
    if (!this.isInitialized) {
      this.reInitVideoElement(videoSource);
      this._htmlCanvasElement = htmlCanvasElementForWebGLRef.nativeElement;
      this._webGL_RendererContext = this._htmlCanvasElement.getContext('webgl2', {
        antialias: false,
        depth: false,
        alpha: false,
        stencil: false,
      });
      this._drawingCanvas = htmlCanvasElementFor2DRef.nativeElement;
      this._2D_RendererContext = htmlCanvasElementFor2DRef.nativeElement.getContext('2d');
      this._intervalMs = intervalMs;

      // multipass
      this._multiPassFrameBuffer = this._webGL_RendererContext.createFramebuffer();
      this._pingPongTextures = [this._createTexture(), this._createTexture()];

      // video program
      const vertexShader = this._createShader(
        this._webGL_RendererContext.VERTEX_SHADER,
        genericVertexShaderSource,
      );
      const fragmentShader = this._createShader(
        this._webGL_RendererContext.FRAGMENT_SHADER,
        videoFragmentShaderSource,
      );
      this._videoProgram = this._createProgram(vertexShader, fragmentShader);
      this._webGLVertexArray = this._setupProgramVertexArrayObject(
        this._videoProgram,
        ContentControlCanvasService.SHARED_VERTICIES,
        ContentControlCanvasService.TEXCOORDS_FLIPPED,
      );

      // video texture
      this._webGLTexture = this._createTexture();
      this._webGL_RendererContext.bindTexture(this._webGL_RendererContext.TEXTURE_2D, this._webGLTexture);
      this._webGL_RendererContext.texParameteri(
        this._webGL_RendererContext.TEXTURE_2D,
        this._webGL_RendererContext.TEXTURE_WRAP_S,
        this._webGL_RendererContext.CLAMP_TO_EDGE,
      );
      this._webGL_RendererContext.texParameteri(
        this._webGL_RendererContext.TEXTURE_2D,
        this._webGL_RendererContext.TEXTURE_WRAP_T,
        this._webGL_RendererContext.CLAMP_TO_EDGE,
      );
      this._webGL_RendererContext.texParameteri(
        this._webGL_RendererContext.TEXTURE_2D,
        this._webGL_RendererContext.TEXTURE_MIN_FILTER,
        this._webGL_RendererContext.LINEAR,
      );
      this._webGL_RendererContext.texParameteri(
        this._webGL_RendererContext.TEXTURE_2D,
        this._webGL_RendererContext.TEXTURE_MAG_FILTER,
        this._webGL_RendererContext.LINEAR,
      );

      const setupVao = this._setupProgramVertexArrayObject.bind(this);

      // effect programs
      this._blackRectProgram = new BlackRectProgram(this._webGL_RendererContext, setupVao);
      this._webGL_RendererContext.bindFramebuffer(this._webGL_RendererContext.FRAMEBUFFER, null);

      this._blackCircleProgram = new BlackCircleProgram(this._webGL_RendererContext, setupVao);
      this._webGL_RendererContext.bindFramebuffer(this._webGL_RendererContext.FRAMEBUFFER, null);

      this._mosaicRectProgram = new MosaicRectProgram(this._webGL_RendererContext, setupVao);
      this._webGL_RendererContext.bindFramebuffer(this._webGL_RendererContext.FRAMEBUFFER, null);

      this._mosaicCircleProgram = new MosaicCircleProgram(this._webGL_RendererContext, setupVao);
      this._webGL_RendererContext.bindFramebuffer(this._webGL_RendererContext.FRAMEBUFFER, null);

      this._blurRectProgram = new BlurRectProgram(this._webGL_RendererContext, setupVao);
      this._webGL_RendererContext.bindFramebuffer(this._webGL_RendererContext.FRAMEBUFFER, null);

      this._blurCircleProgram = new BlurCircleProgram(this._webGL_RendererContext, setupVao);
      this._webGL_RendererContext.bindFramebuffer(this._webGL_RendererContext.FRAMEBUFFER, null);

      // effect area canvas
      this.resetDrawEffectArea();
    }
  }
  public startRender() {
    this.stopRender();
    this._renderRafId = requestAnimationFrame(this._render.bind(this));
  }

  public stopRender() {
    if (this._renderRafId !== null) {
      cancelAnimationFrame(this._renderRafId);
      this._renderRafId = null;
    }
  }

  public addBlackRectangle(br: CCRect): boolean {
    return this._blackRectCollection.add(br);
  }
  public updateBlackRectangle(r: CCRect): void {
    this._blackRectCollection.update(r);
  }
  public removeBlackRectangle(id: number): boolean {
    return this._blackRectCollection.remove(id);
  }
  public addBlackCircle(br: CCRect): boolean {
    return this._blackCircleCollection.add(br);
  }
  public updateBlackCircle(r: CCRect): void {
    this._blackCircleCollection.update(r);
  }
  public removeBlackCircle(id: number): boolean {
    return this._blackCircleCollection.remove(id);
  }
  public addMosaicRectangle(br: CCMosaicRect): boolean {
    return this._mosaicRectCollection.add(br);
  }
  public updateMosaicRectangle(r: CCMosaicRect): void {
    this._mosaicRectCollection.update(r);
  }
  public removeMosaicRectangle(id: number): boolean {
    return this._mosaicRectCollection.remove(id);
  }
  public addMosaicCircle(br: CCMosaicRect): boolean {
    return this._mosaicCircleCollection.add(br);
  }
  public updateMosaicCircle(r: CCMosaicRect): void {
    this._mosaicCircleCollection.update(r);
  }
  public removeMosaicCircle(id: number): boolean {
    return this._mosaicCircleCollection.remove(id);
  }

  public addBlurRectangle(blurRectangle: CCBlurRect): boolean {
    return this._blurRectCollection.add(blurRectangle);
  }
  public updateBlurRectangle(blurRectangle: CCBlurRect): void {
    this._blurRectCollection.update(blurRectangle);
  }
  public removeBlurRectangle(id: number): boolean {
    return this._blurRectCollection.remove(id);
  }

  public addBlurCircle(br: CCBlurRect): boolean {
    return this._blurCircleCollection.add(br);
  }
  public updateBlurCircle(r: CCBlurRect): void {
    this._blurCircleCollection.update(r);
  }
  public removeBlurCircle(id: number): boolean {
    return this._blurCircleCollection.remove(id);
  }

  public clearAllRenderingObjects() {
    this._blackRectCollection.clear();
    this._mosaicRectCollection.clear();
    this._blurRectCollection.clear();
    this._blackCircleCollection.clear();
    this._blurCircleCollection.clear();
    this._mosaicCircleCollection.clear();
    this.resetDrawEffectArea();
    this.drawEffectAreaSVG = false;
    this.drawMultiEffects = [];
    this._subtitles = [];
  }

  public resetDrawEffectArea(
    toObject?: CanvasBaseObject | PlacementOnCanvas,
    highlight: string = '',
    scaleFactor?: number,
  ) {
    const { x, y, width, height } = this._scaleBoundingBox(
      toObject || this.getEffectAreaDefaultPosition(),
      scaleFactor || this._scalingFactor,
    );
    this.drawEffectRect = new CCdrawingAreaRect(
      ContentControlCanvasService.EFFECT_AREA_ID,
      x * this.drawingCanvasWidthUnit,
      y * this.drawingCanvasHeightUnit,
      width * this.drawingCanvasWidthUnit,
      height * this.drawingCanvasHeightUnit,
      highlight,
    );
  }
  public addTempDrawEffectArea(
    id: number,
    toObject: CanvasBaseObject | PlacementOnCanvas,
    scaleFactor?: number,
  ) {
    if (this.drawMultiEffects.findIndex((x) => x.id === id) > -1) return;
    const { x, y, width, height } = this._scaleBoundingBox(
      toObject,
      scaleFactor || this._scalingFactor,
    );
    const tempArea = new CCdrawingAreaRect(
      id,
      x * this.drawingCanvasWidthUnit,
      y * this.drawingCanvasHeightUnit,
      width * this.drawingCanvasWidthUnit,
      height * this.drawingCanvasHeightUnit,
      'temp',
    );
    this.drawMultiEffects.push(tempArea);
  }

  public resizeDrawEffectArea(
    x: number,
    y: number,
    width: number,
    height: number,
  ) {
    this.drawEffectRect.x = x * this.drawingCanvasWidthUnit;
    this.drawEffectRect.y = y * this.drawingCanvasHeightUnit;
    this.drawEffectRect.width = width * this.drawingCanvasWidthUnit;
    this.drawEffectRect.height = height * this.drawingCanvasHeightUnit;
  }
  public updateSubtitles(subs: Array<string[]>): void {
    this._subtitles = subs;
  }
  private _applyEffect(
    effectProgram: EffectProgram,
    collection: ShapeCollection<CanvasBaseObject>,
    inputTexture: WebGLTexture,
  ): WebGLTexture {
    if (!this._webGL_RendererContext.isTexture(inputTexture)) {
      console.error('Invalid input texture');
      return null;
    }
    const outputTextureIndex = 1 - this._currentPingPongIndex;
    const program = effectProgram.program;
    const vao = effectProgram.vao;

    this._webGL_RendererContext.bindFramebuffer(this._webGL_RendererContext.FRAMEBUFFER, this._multiPassFrameBuffer);
    this._webGL_RendererContext.framebufferTexture2D(
      this._webGL_RendererContext.FRAMEBUFFER,
      this._webGL_RendererContext.COLOR_ATTACHMENT0,
      this._webGL_RendererContext.TEXTURE_2D,
      this._pingPongTextures[outputTextureIndex],
      0,
    );

    const status = this._webGL_RendererContext.checkFramebufferStatus(this._webGL_RendererContext.FRAMEBUFFER);
    if (status !== this._webGL_RendererContext.FRAMEBUFFER_COMPLETE) {
      console.error('Framebuffer is not complete:', status);
      return null;
    }

    this._webGL_RendererContext.viewport(0, 0, this._webGL_RendererContext.canvas.width, this._webGL_RendererContext.canvas.height);

    this._webGL_RendererContext.useProgram(program);
    if (this._webGL_RendererContext.getParameter(this._webGL_RendererContext.CURRENT_PROGRAM) !== program) {
      console.error('Failed to use the correct program');
      return null;
    }

    this._webGL_RendererContext.bindVertexArray(vao);

    this._webGL_RendererContext.activeTexture(this._webGL_RendererContext.TEXTURE0);
    this._webGL_RendererContext.bindTexture(this._webGL_RendererContext.TEXTURE_2D, inputTexture);

    const textureLocation = this._webGL_RendererContext.getUniformLocation(program, 'u_texture');
    if (textureLocation === null) {
      console.error('Unable to get uniform location for u_texture');
    } else {
      this._webGL_RendererContext.uniform1i(textureLocation, 0);
    }

    const items = [...collection.items];
    effectProgram.setupUniforms(
      items,
      this._transformRectPosition.bind(this),
      this._transformRectSize.bind(this),
      this._calculateGaussianKernel.bind(this),
    );
    collection.clear();

    this._webGL_RendererContext.drawArrays(this._webGL_RendererContext.TRIANGLES, 0, 6);

    const error = this._webGL_RendererContext.getError();
    if (error !== this._webGL_RendererContext.NO_ERROR) {
      console.error('WebGL error after draw:', error);
    }

    this._webGL_RendererContext.bindVertexArray(null);
    this._webGL_RendererContext.bindFramebuffer(this._webGL_RendererContext.FRAMEBUFFER, null);

    // Swap ping-pong index
    this._currentPingPongIndex = outputTextureIndex;
    return this._pingPongTextures[this._currentPingPongIndex];
  }
  public postResizeActions() {
    this._webGLTexture = this._createTexture();
    this._pingPongTextures = [this._createTexture(), this._createTexture()];
  }
  private _recalculateDrawEffectAreaWidthAndHeight() {
    const screenWidth = this.canvasWidth;
    const rawHeight = this.canvasHeight;
    const aspectRatio = ContentControlCanvasService.EFFECT_AREA_ASPECT_RATIO;
    const screenHeight =
      screenWidth / rawHeight !== aspectRatio ? screenWidth / aspectRatio : rawHeight;

    const squareSize = screenHeight * ContentControlCanvasService.EFFECT_AREA_SQUARE_FACTOR;

    // Calculate the square's dimensions as percentages of screen width and height
    const widthPercentage = (squareSize / screenWidth) * 100;

    // Calculate the left offset to center the square
    const leftOffset = (screenWidth - squareSize) / 2;
    const leftPercentage = (leftOffset / screenWidth) * 100;

    // Calculate the top offset to center the square vertically
    const topOffset = (screenHeight - squareSize) / 2;
    const topPercentage = (topOffset / screenHeight) * 100;

    this._effectAreaDefaultPosition = new PlacementOnCanvas(
      leftPercentage,
      topPercentage,
      widthPercentage,
      ContentControlCanvasService.EFFECT_AREA_DEFAULT_HEIGHT_PERCENT,
    );
  }
  private _render(currentTime: number) {
    if (
      currentTime - this._lastFrameTime >= this._intervalMs ||
      this._videoElement.paused
    ) {
      this._renderWebGL();
      this._render2DOverlays();
      this._lastFrameTime = currentTime;
    }
    this._renderRafId = requestAnimationFrame(this._render.bind(this));
  }
  private _renderWebGL() {
    //clear canvases
    this._2D_RendererContext.clearRect(
      0,
      0,
      this._drawingCanvas.width,
      this._drawingCanvas.height,
    );
    this._webGL_RendererContext.clear(this._webGL_RendererContext.COLOR_BUFFER_BIT);

    // Update video texture
    this._webGL_RendererContext.bindTexture(this._webGL_RendererContext.TEXTURE_2D, this._webGLTexture);
    this._webGL_RendererContext.pixelStorei(this._webGL_RendererContext.UNPACK_FLIP_Y_WEBGL, true);
    this._webGL_RendererContext.texImage2D(
      this._webGL_RendererContext.TEXTURE_2D,
      0,
      this._webGL_RendererContext.RGBA,
      this._webGL_RendererContext.RGBA,
      this._webGL_RendererContext.UNSIGNED_BYTE,
      this._videoElement.getTexImageSource()
    );
    this._webGL_RendererContext.pixelStorei(this._webGL_RendererContext.UNPACK_FLIP_Y_WEBGL, false);
    // Copy video to framebuffer texture
    this._copyVideoToMultipassFramebuffer();

    let currentTexture = this._pingPongTextures[this._currentPingPongIndex];

    // Draw blur rectangles
    if (this._blurRectCollection.length > 0) {
      currentTexture = this._applyEffect(this._blurRectProgram, this._blurRectCollection, currentTexture);
    }

    // Draw blur circles
    if (this._blurCircleCollection.length > 0) {
      currentTexture = this._applyEffect(this._blurCircleProgram, this._blurCircleCollection, currentTexture);
    }

    // Draw mosaic rectangles
    if (this._mosaicRectCollection.length > 0) {
      currentTexture = this._applyEffect(this._mosaicRectProgram, this._mosaicRectCollection, currentTexture);
    }

    // Draw mosaic circles
    if (this._mosaicCircleCollection.length > 0) {
      currentTexture = this._applyEffect(this._mosaicCircleProgram, this._mosaicCircleCollection, currentTexture);
    }

    // Draw black circles
    if (this._blackCircleCollection.length > 0) {
      currentTexture = this._applyEffect(this._blackCircleProgram, this._blackCircleCollection, currentTexture);
    }

    // Draw black rectangles
    if (this._blackRectCollection.length > 0) {
      currentTexture = this._applyEffect(this._blackRectProgram, this._blackRectCollection, currentTexture);
    }

    // Unbind the framebuffer to render to the canvas
    this._webGL_RendererContext.bindFramebuffer(this._webGL_RendererContext.FRAMEBUFFER, null);

    // Render final result to canvas
    this._webGL_RendererContext.viewport(0, 0, this._webGL_RendererContext.canvas.width, this._webGL_RendererContext.canvas.height);
    this._webGL_RendererContext.useProgram(this._videoProgram);
    this._webGL_RendererContext.bindVertexArray(this._webGLVertexArray);
    this._webGL_RendererContext.activeTexture(this._webGL_RendererContext.TEXTURE0);
    this._webGL_RendererContext.bindTexture(this._webGL_RendererContext.TEXTURE_2D, currentTexture);
    this._webGL_RendererContext.uniform1i(
      this._webGL_RendererContext.getUniformLocation(this._videoProgram, 'u_texture'),
      0,
    );
    this._webGL_RendererContext.drawArrays(this._webGL_RendererContext.TRIANGLES, 0, 6);

    this._webGL_RendererContext.bindVertexArray(null);
    this._webGL_RendererContext.bindBuffer(this._webGL_RendererContext.ARRAY_BUFFER, null);
    // Unbind video VAO and buffer
    this._webGL_RendererContext.bindVertexArray(null);
    this._webGL_RendererContext.bindBuffer(this._webGL_RendererContext.ARRAY_BUFFER, null);
  }
  private _render2DOverlays() {
    ////// start drawing canvas draws
    //subtitles
    this._subtitles.length > 0 && this._drawCenteredSubtitles();
    // draw effect area SVG
    if (this.drawEffectAreaSVG) {
      this._drawEffectArea(this.drawEffectRect);
      this.drawMultiEffects.length > 0 &&
        this.drawMultiEffects.map(this._drawEffectArea.bind(this));
      this.drawMultiEffects = [];
      this.drawEffectAreaSVG = false;
    }
  }
  private _drawCenteredSubtitles() {
    this._2D_RendererContext.textAlign = 'center';
    this._2D_RendererContext.textBaseline = 'middle';
    this._2D_RendererContext.font = `${ContentControlCanvasService.SUBTITLE_FONT_SIZE}px Arial`;
    this._2D_RendererContext.fillStyle = 'white';
    this._2D_RendererContext.shadowColor = 'black';
    this._2D_RendererContext.shadowBlur = 4;
    this._2D_RendererContext.shadowOffsetX = 2;
    this._2D_RendererContext.shadowOffsetY = 2;
    let currentY = this._drawingCanvas.height - ContentControlCanvasService.SUBTITLE_BOTTOM_OFFSET;
    this._subtitles.forEach((group) => {
      const splitGroup = group.flatMap((line) =>
        line.replaceAll('\r\n', '\n').split('\n'),
      );

      if (splitGroup.length > 2) {
        currentY -= (splitGroup.length - 2) * ContentControlCanvasService.SUBTITLE_LINE_HEIGHT;
      }
      splitGroup.forEach((line, lineIndex) => {
        this._2D_RendererContext.direction = ContentControlCanvasService.RTLCHARS.test(line)
          ? 'rtl'
          : 'ltr';
        this._2D_RendererContext.fillText(
          line,
          this._drawingCanvas.width / 2,
          currentY + lineIndex * ContentControlCanvasService.SUBTITLE_LINE_SPACING,
        );
      });
      currentY -= splitGroup.length * ContentControlCanvasService.SUBTITLE_LINE_HEIGHT;
    });
  }
  private _createShader(type: GLenum, source: string): WebGLShader | null {
    const shader = this._webGL_RendererContext.createShader(type);
    this._webGL_RendererContext.shaderSource(shader, source);
    this._webGL_RendererContext.compileShader(shader);
    if (!this._webGL_RendererContext.getShaderParameter(shader, this._webGL_RendererContext.COMPILE_STATUS)) {
      console.error(this._webGL_RendererContext.getShaderInfoLog(shader));
      this._webGL_RendererContext.deleteShader(shader);
      return null;
    }
    return shader;
  }
  private _createProgram(
    vertexShader: WebGLShader | null,
    fragmentShader: WebGLShader | null,
  ): WebGLProgram | null {
    const program = this._webGL_RendererContext.createProgram();
    this._webGL_RendererContext.attachShader(program, vertexShader);
    this._webGL_RendererContext.attachShader(program, fragmentShader);
    this._webGL_RendererContext.linkProgram(program);

    if (!this._webGL_RendererContext.getProgramParameter(program, this._webGL_RendererContext.LINK_STATUS)) {
      console.error(this._webGL_RendererContext.getProgramInfoLog(program));
      this._webGL_RendererContext.deleteProgram(program);
      return null;
    }
    this._webGL_RendererContext.validateProgram(program);
    if (!this._webGL_RendererContext.getProgramParameter(program, this._webGL_RendererContext.VALIDATE_STATUS)) {
      console.error(
        'Unable to validate the shader program:',
        this._webGL_RendererContext.getProgramInfoLog(program),
      );
      return null;
    }
    return program;
  }

  private _createBuffer(data: ArrayLike<number>): WebGLBuffer {
    const buffer = this._webGL_RendererContext.createBuffer();
    this._webGL_RendererContext.bindBuffer(this._webGL_RendererContext.ARRAY_BUFFER, buffer);
    this._webGL_RendererContext.bufferData(
      this._webGL_RendererContext.ARRAY_BUFFER,
      new Float32Array(data),
      this._webGL_RendererContext.STATIC_DRAW,
    );
    return buffer;
  }
  private _createTexture(): WebGLTexture {
    const texture = this._webGL_RendererContext.createTexture();
    this._webGL_RendererContext.bindTexture(this._webGL_RendererContext.TEXTURE_2D, texture);
    this._webGL_RendererContext.texImage2D(
      this._webGL_RendererContext.TEXTURE_2D,
      0,
      this._webGL_RendererContext.RGBA,
      this._webGL_RendererContext.canvas.width,
      this._webGL_RendererContext.canvas.height,
      0,
      this._webGL_RendererContext.RGBA,
      this._webGL_RendererContext.UNSIGNED_BYTE,
      null,
    );
    this._webGL_RendererContext.texParameteri(
      this._webGL_RendererContext.TEXTURE_2D,
      this._webGL_RendererContext.TEXTURE_MIN_FILTER,
      this._webGL_RendererContext.LINEAR,
    );
    this._webGL_RendererContext.texParameteri(
      this._webGL_RendererContext.TEXTURE_2D,
      this._webGL_RendererContext.TEXTURE_MAG_FILTER,
      this._webGL_RendererContext.LINEAR,
    );
    this._webGL_RendererContext.texParameteri(
      this._webGL_RendererContext.TEXTURE_2D,
      this._webGL_RendererContext.TEXTURE_WRAP_S,
      this._webGL_RendererContext.CLAMP_TO_EDGE,
    );
    this._webGL_RendererContext.texParameteri(
      this._webGL_RendererContext.TEXTURE_2D,
      this._webGL_RendererContext.TEXTURE_WRAP_T,
      this._webGL_RendererContext.CLAMP_TO_EDGE,
    );
    return texture;
  }
  private _setupProgramVertexArrayObject(
    webGL_Program: WebGLProgram,
    positions: Float32Array = ContentControlCanvasService.SHARED_VERTICIES,
    texcoords: Float32Array = ContentControlCanvasService.TEXCOORDS_NORMAL,
  ): WebGLVertexArrayObject {
    const newVertextArray = this._webGL_RendererContext.createVertexArray();
    this._webGL_RendererContext.bindVertexArray(newVertextArray);

    // Position attribute
    this._createBuffer(positions);
    const positionLocation = this._webGL_RendererContext.getAttribLocation(webGL_Program, 'a_position');
    this._webGL_RendererContext.enableVertexAttribArray(positionLocation);
    this._webGL_RendererContext.vertexAttribPointer(
      positionLocation,
      2,
      this._webGL_RendererContext.FLOAT,
      false,
      0,
      0,
    );

    // Texcoord attribute
    this._createBuffer(texcoords);
    const texcoordLocation = this._webGL_RendererContext.getAttribLocation(webGL_Program, 'a_texcoord');
    this._webGL_RendererContext.enableVertexAttribArray(texcoordLocation);
    this._webGL_RendererContext.vertexAttribPointer(
      texcoordLocation,
      2,
      this._webGL_RendererContext.FLOAT,
      false,
      0,
      0,
    );

    this._webGL_RendererContext.bindVertexArray(null);
    return newVertextArray;
  }
  private _copyVideoToMultipassFramebuffer() {
    this._webGL_RendererContext.bindFramebuffer(this._webGL_RendererContext.FRAMEBUFFER, this._multiPassFrameBuffer);
    this._webGL_RendererContext.framebufferTexture2D(
      this._webGL_RendererContext.FRAMEBUFFER,
      this._webGL_RendererContext.COLOR_ATTACHMENT0,
      this._webGL_RendererContext.TEXTURE_2D,
      this._pingPongTextures[0],
      0,
    );
    this._webGL_RendererContext.viewport(0, 0, this._webGL_RendererContext.canvas.width, this._webGL_RendererContext.canvas.height);

    this._webGL_RendererContext.useProgram(this._videoProgram);
    this._webGL_RendererContext.bindVertexArray(this._webGLVertexArray);

    this._webGL_RendererContext.activeTexture(this._webGL_RendererContext.TEXTURE0);
    this._webGL_RendererContext.bindTexture(this._webGL_RendererContext.TEXTURE_2D, this._webGLTexture);
    this._webGL_RendererContext.uniform1i(
      this._webGL_RendererContext.getUniformLocation(this._videoProgram, 'u_texture'),
      0,
    );

    this._webGL_RendererContext.drawArrays(this._webGL_RendererContext.TRIANGLES, 0, 6);

    this._webGL_RendererContext.bindVertexArray(null);
    this._currentPingPongIndex = 0;
  }
  private _scaleBoundingBox(
    rect: CCRect | PlacementOnCanvas,
    scaleFactor?: number,
  ): PlacementOnCanvas {
    const factor = scaleFactor ?? this._scalingFactor ?? 1.0;
    // Extract the top-left corner and dimensions
    const { x, y, width, height } = rect;

    // Calculate new width and height
    const newWidth = width * factor;
    const newHeight = height * factor;

    // Calculate the center of the original bounding box
    const centerX = x + width / 2;
    const centerY = y + height / 2;

    // Calculate the new top-left corner to maintain the center
    const newX = centerX - newWidth / 2;
    const newY = centerY - newHeight / 2;

    // Return the new bounding box
    return new PlacementOnCanvas(newX, newY, newWidth, newHeight);
  }
  private _transformRectPosition(rect: CCRect): number[] {
    const { x, y } = this._scaleBoundingBox(
      rect,
      rect.scaleFactor || this._scalingFactor,
    );
    return [x * 0.01, y * 0.01];
  }
  private _transformRectSize(rect: CCRect): number[] {
    const { width, height } = this._scaleBoundingBox(
      rect,
      rect.scaleFactor || this._scalingFactor,
    );
    return [width * 0.01, height * 0.01];
  }
  public updateScalingFactor(factor: number): void {
    this._scalingFactor = factor;
  }
  public drawEffectAreaClicked(
    cx: number,
    cy: number,
  ): EffectAreaHitResult {
    for (const property in this.effectRectAreaCorners) {
      const corner = this.effectRectAreaCorners[property];
      if (
        this._checkInBound(
          cx,
          corner.x,
          cy,
          corner.y,
          ContentControlCanvasService.EFFECT_AREA_CORNER_SIZE,
          ContentControlCanvasService.EFFECT_AREA_CORNER_SIZE,
        )
      ) {
        return property as EffectAreaCornerKey;
      }
    }
    if (
      this._checkInBound(
        cx,
        this.drawEffectRect.x,
        cy,
        this.drawEffectRect.y,
        this.drawEffectRect.width,
        this.drawEffectRect.height,
      )
    ) {
      return 'frame';
    }
    return '';
  }

  private _checkInBound(cx: number, x: number, cy: number, y: number, width: number, height: number): boolean {
    return cx >= x && cx <= x + width && cy >= y && cy <= y + height;
  }

  private _getEffectRectCorners(
    rect: CCdrawingAreaRect,
  ): Record<EffectAreaCornerKey, CanvasPoint> {
    const { x, y, width, height } = rect;
    return {
      'top-left': new CanvasPoint(x, y),
      'top-middle': new CanvasPoint(x + width / 2, y),
      'top-right': new CanvasPoint(x + width, y),
      'right-middle': new CanvasPoint(x + width, y + height / 2),
      'right-bottom': new CanvasPoint(x + width, y + height),
      'bottom-middle': new CanvasPoint(x + width / 2, y + height),
      'bottom-left': new CanvasPoint(x, y + height),
      'left-middle': new CanvasPoint(x, y + height / 2),
    };
  }

  private _drawEffectArea(drawEffectRect: CCdrawingAreaRect) {
    this._2D_RendererContext.strokeStyle =
      drawEffectRect.highlightArea === 'tween' ? '#A2E0FD' : 'white';
    this._2D_RendererContext.fillStyle = 'transparent';
    this._2D_RendererContext.lineWidth =
      drawEffectRect.highlightArea === 'frame'
        ? ContentControlCanvasService.EFFECT_AREA_FRAME_LINE_WIDTH
        : ContentControlCanvasService.EFFECT_AREA_DEFAULT_LINE_WIDTH;

    this._2D_RendererContext.strokeRect(
      drawEffectRect.x,
      drawEffectRect.y,
      drawEffectRect.width,
      drawEffectRect.height,
    );

    const cornerSize = ContentControlCanvasService.EFFECT_AREA_CORNER_SIZE;
    const half = cornerSize / 2;
    const corners = this._getEffectRectCorners(drawEffectRect);
    for (const property of Object.keys(corners)) {
      const center = corners[property];
      const hitX = center.x - half;
      const hitY = center.y - half;
      this.effectRectAreaCorners[property as EffectAreaCornerKey] = new CanvasPoint(hitX, hitY);
      this._2D_RendererContext.lineWidth =
        drawEffectRect.highlightArea === property
          ? ContentControlCanvasService.EFFECT_AREA_FRAME_LINE_WIDTH
          : ContentControlCanvasService.EFFECT_AREA_DEFAULT_LINE_WIDTH;
      this._2D_RendererContext.strokeRect(hitX, hitY, cornerSize, cornerSize);
    }

    // set cross
    const centerX = drawEffectRect.x + drawEffectRect.width / 2;
    const centerY = drawEffectRect.y + drawEffectRect.height / 2;
    const crossArmLength = Math.min(
      drawEffectRect.width * ContentControlCanvasService.EFFECT_AREA_CROSS_ARM_FACTOR,
      ContentControlCanvasService.EFFECT_AREA_CROSS_ARM_MAX,
    );
    const crossWidth = crossArmLength;
    const crossHeight = crossArmLength;
    this._2D_RendererContext.lineWidth = ContentControlCanvasService.EFFECT_AREA_DEFAULT_LINE_WIDTH;

    // draw cross horizontal line
    this._2D_RendererContext.beginPath();
    this._2D_RendererContext.moveTo(centerX - crossWidth / 2, centerY);
    this._2D_RendererContext.lineTo(centerX + crossWidth / 2, centerY);
    this._2D_RendererContext.stroke();

    // draw cross vertical line
    this._2D_RendererContext.beginPath();
    this._2D_RendererContext.moveTo(centerX, centerY - crossHeight / 2);
    this._2D_RendererContext.lineTo(centerX, centerY + crossHeight / 2);
    this._2D_RendererContext.stroke();
  }

  private _calculateGaussianKernel(maxRadius: number): Float32Array {
    const size = 2 * maxRadius + 1;
    const kernel = new Float32Array(size * size);
    let sum = 0.0;
    const sigma = maxRadius / ContentControlCanvasService.GAUSSIAN_SIGMA_DIVISOR;

    for (let y = -maxRadius; y <= maxRadius; y++) {
      for (let x = -maxRadius; x <= maxRadius; x++) {
        const exponent = -(x * x + y * y) / (2 * sigma * sigma);
        const weight = Math.exp(exponent) / (2 * Math.PI * sigma * sigma);
        kernel[(y + maxRadius) * size + (x + maxRadius)] = weight;
        sum += weight;
      }
    }

    // Normalize the kernel
    for (let i = 0; i < kernel.length; i++) {
      kernel[i] /= sum;
    }

    return kernel;
  }
}
